import type { AxiosInstance } from "axios";

import type { IncidentWorkItemStatus } from "@thalamos/common";
import { parseAxiosResponse } from "@thalamos/common";
import type {
  AdminEditReportingFieldsRequestUnbranded,
  AdminRelinkPlaceOfSafetyRequestUnbranded,
  CreateIncidentMinimumFields,
  CreateIncidentWorkItemRequest,
  CreateTransferRequestUnbranded,
  FormSection,
  FormSectionSchema,
  GetAuditTrailEntriesResponse,
  GetIncidentWorkItemResponse,
  IncidentFormId,
  IncidentWorkItemId,
  IncidentWorkItemIdUnbranded,
  IncidentWorkItemWithContext,
  ResolveTransferRequestUnbranded,
  RevokeTransferRequestUnbranded,
  SearchIncidentWorkItemRequest,
  SearchIncidentWorkItemResponse,
  UserId,
} from "@thalamos/vision-common";
import {
  GetAuditTrailEntriesResponseSchema,
  GetIncidentWorkItemResponseSchema,
  IncidentWorkItemWithContextSchema,
  notificationChannelFns,
  SearchIncidentWorkItemResponseSchema,
} from "@thalamos/vision-common";

import type { ErrorBody, NoContentBody, ResponseBody } from "../axios/api.js";
import { useApiAxios, usePusherChannelReload } from "../axios/api.js";

const parseResponseIncidentWorkItemWithContext = parseAxiosResponse(
  IncidentWorkItemWithContextSchema,
);

const parseResponseGetIncidentWorkItem = parseAxiosResponse(
  GetIncidentWorkItemResponseSchema,
);

const parseResponseSearchIncidentWorkItem = parseAxiosResponse(
  SearchIncidentWorkItemResponseSchema,
);

const parseResponseGetAuditTrailEntries = parseAxiosResponse(
  GetAuditTrailEntriesResponseSchema,
);

export const config = {
  incidentWorkItem: {
    get: (id: IncidentWorkItemId) => ({
      url: `/api/incident-work-item/${id}`,
      method: "get",
      transformResponse: parseResponseIncidentWorkItemWithContext,
    }),
    getPdf: (id: IncidentWorkItemId) => ({
      url: `/api/incident-work-item/${id}/pdf`,
      method: "get",
    }),
    getMyActiveIncidentWorkItems: () => ({
      url: `/api/incident-work-item`,
      method: "get",
      transformResponse: parseResponseGetIncidentWorkItem,
    }),
    getArchiveForms: () => ({
      url: `/api/incident-work-item/archive`,
      method: "get",
      transformResponse: parseResponseGetIncidentWorkItem,
    }),
    create: (
      id: IncidentWorkItemId,
      creatIncidentWorkItemReq: CreateIncidentWorkItemRequest<CreateIncidentMinimumFields>,
    ) => ({
      url: `/api/incident-work-item/${id}`,
      method: "post",
      data: creatIncidentWorkItemReq,
    }),
    share: (id: IncidentWorkItemIdUnbranded, email?: string[]) => ({
      url: `/api/incident-work-item/${id}/share`,
      method: "post",
      data: { email },
    }),
    updateData(
      id: IncidentWorkItemId,
      formId: IncidentFormId,
      section: FormSection,
      data: Record<string, unknown>,
      validateData: boolean,
      status?: IncidentWorkItemStatus,
    ) {
      return {
        url: `/api/incident-work-item/${id}/form/${formId}/section/${section}`,
        method: "post",
        data,
        params: { validateData, status },
      };
    },
    create136Form(id: IncidentWorkItemId) {
      return {
        url: `/api/incident-work-item/${id}/form/136`,
        method: "post",
      };
    },
    createMentalCapacityActForm(id: IncidentWorkItemId) {
      return {
        url: `/api/incident-work-item/${id}/form/mental-capacity-act`,
        method: "post",
      };
    },
    createPlaceOfSafetyForm(
      workItemId: IncidentWorkItemId,
      formId: IncidentFormId,
    ) {
      return {
        url: `/api/incident-work-item/${workItemId}/form/place-of-safety/${formId}`,
        method: "post",
      };
    },
    createAndPopulatePlaceOfSafety(
      workItemId: IncidentWorkItemId,
      formId: IncidentFormId,
      data: Record<string, unknown>,
    ) {
      return {
        url: `/api/incident-work-item/${workItemId}/form/place-of-safety/${formId}/create-and-populate`,
        method: "post",
        data: { data },
      };
    },
    upsertPlaceOfSafety(
      workItemId: IncidentWorkItemId,
      formId: IncidentFormId,
      body: {
        createForm: boolean;
        mainSectionData: Record<string, unknown>;
        mainSectionValidateData: boolean;
        arrivalSectionData?: Record<string, unknown> | null;
        arrivalSectionValidateData?: boolean;
        arrivalStatus?: IncidentWorkItemStatus;
        leaveSectionData?: Record<string, unknown> | null;
        leaveSectionValidateData?: boolean;
      },
    ) {
      return {
        url: `/api/incident-work-item/${workItemId}/form/place-of-safety/${formId}/upsert`,
        method: "post",
        data: body,
      };
    },
    recordPolicePresenceRequestAsOfficer(
      workItemId: IncidentWorkItemId,
      formId: IncidentFormId,
      data: {
        [FormSectionSchema.enum.requestPolicePresenceMain]: Record<
          string,
          unknown
        >;
        [FormSectionSchema.enum.recordPolicePresence]?: Record<string, unknown>;
      },
    ) {
      return {
        url: `/api/incident-work-item/${workItemId}/form/police-presence-request/${formId}`,
        method: "post",
        data,
      };
    },
    updateActiveOfficerRequest(
      incidentWorkItemId: IncidentWorkItemId,
      officerRequestFormId: IncidentFormId | null,
    ) {
      return {
        url: `/api/incident-work-item/${incidentWorkItemId}/active-officer-request`,
        method: "post",
        data: { formId: officerRequestFormId },
      };
    },
    createPreEndIncidentForm(
      workItemId: IncidentWorkItemId,
      formId: IncidentFormId,
    ) {
      return {
        url: `/api/incident-work-item/${workItemId}/form/pre-end-incident/${formId}`,
        method: "post",
      };
    },

    createFinishForm(workItemId: IncidentWorkItemId, formId: IncidentFormId) {
      return {
        url: `/api/incident-work-item/${workItemId}/form/finish-incident/${formId}`,
        method: "post",
      };
    },

    endIncident(workItemId: IncidentWorkItemId) {
      return {
        url: `/api/incident-work-item/${workItemId}/end-incident`,
        method: "post",
      };
    },

    preEndAndCloseIncident(
      workItemId: IncidentWorkItemId,
      formId: IncidentFormId,
      sectionId: FormSection,
      data: Record<string, unknown>,
    ) {
      return {
        url: `/api/incident-work-item/${workItemId}/pre-end-and-close`,
        method: "post",
        data: { formId, sectionId, data },
      };
    },

    finishAndCloseIncident(
      workItemId: IncidentWorkItemId,
      formId: IncidentFormId,
      sectionId: FormSection,
      data: Record<string, unknown>,
    ) {
      return {
        url: `/api/incident-work-item/${workItemId}/finish-and-close`,
        method: "post",
        data: { formId, sectionId, data },
      };
    },

    submitTriage(
      workItemId: IncidentWorkItemId,
      formId: IncidentFormId,
      sectionId: FormSection,
      data: Record<string, unknown>,
    ) {
      return {
        url: `/api/incident-work-item/${workItemId}/submit-triage`,
        method: "post",
        data: { formId, sectionId, data },
      };
    },

    createTransfer(createTransfer: CreateTransferRequestUnbranded) {
      return {
        url: `/api/transfer/create/`,
        method: "post",
        data: createTransfer,
      };
    },
    resolveTransfer(resolveTransfer: ResolveTransferRequestUnbranded) {
      return {
        url: `/api/transfer/resolve`,
        method: "post",
        data: resolveTransfer,
      };
    },
    revokeTransfer(incidentWorkItemId: RevokeTransferRequestUnbranded) {
      return {
        url: `/api/transfer/revoke`,
        method: "post",
        data: incidentWorkItemId,
      };
    },
    adminRevokeTransfer(incidentWorkItemId: RevokeTransferRequestUnbranded) {
      return {
        url: `/api/transfer/admin/revoke`,
        method: "post",
        data: incidentWorkItemId,
      };
    },
    search: (filters: SearchIncidentWorkItemRequest) => {
      return {
        url: `/api/incident-work-item/search`,
        method: "post",
        data: filters,
        transformResponse: parseResponseSearchIncidentWorkItem,
      };
    },
    createSnapshotDownload: (id: IncidentWorkItemId) => ({
      url: `/api/incident-work-item/${id}/snapshots/download`,
      method: "post",
    }),
    pollSnapshotDownload: (id: IncidentWorkItemId, jobId: string) => ({
      url: `/api/incident-work-item/${id}/snapshots/download/${jobId}`,
      method: "get",
    }),
    getSummary: (id: IncidentWorkItemId) => ({
      url: `/api/incident-work-item/${id}/summary`,
      method: "get",
      transformResponse: parseResponseIncidentWorkItemWithContext,
    }),
    getAuditTrail: (
      id: IncidentWorkItemId,
      params: { limit?: number; offset?: number } = {},
    ) => ({
      url: `/api/incident-work-item/${id}/audit-trail`,
      method: "get",
      params,
      transformResponse: parseResponseGetAuditTrailEntries,
    }),
    adminEditReportingFields: (
      id: IncidentWorkItemId,
      body: AdminEditReportingFieldsRequestUnbranded,
    ) => ({
      url: `/api/incident-work-item/${id}/admin-edit-reporting-fields`,
      method: "post",
      data: body,
    }),
    adminRelinkPlaceOfSafety: (
      id: IncidentWorkItemId,
      body: AdminRelinkPlaceOfSafetyRequestUnbranded,
    ) => ({
      url: `/api/incident-work-item/${id}/admin-relink-place-of-safety`,
      method: "post",
      data: body,
    }),
  },
};

export const api = (apiAxios: AxiosInstance) => {
  return {
    incidentWorkItem: {
      get: (id: IncidentWorkItemId) =>
        apiAxios.request<IncidentWorkItemWithContext | null>(
          config.incidentWorkItem.get(id),
        ),
      getPdf: (id: IncidentWorkItemId) =>
        apiAxios.request<string>(config.incidentWorkItem.getPdf(id)),

      getMyActiveIncidentWorkItems: () =>
        apiAxios.request<GetIncidentWorkItemResponse>(
          config.incidentWorkItem.getMyActiveIncidentWorkItems(),
        ),

      create: (
        id: IncidentWorkItemId,
        creatIncidentWorkItemReq: CreateIncidentWorkItemRequest<CreateIncidentMinimumFields>,
      ) =>
        apiAxios.request<ResponseBody<NoContentBody, ErrorBody>>(
          config.incidentWorkItem.create(id, creatIncidentWorkItemReq),
        ),

      share: (id: IncidentWorkItemIdUnbranded, email?: string[]) =>
        apiAxios.request(config.incidentWorkItem.share(id, email)),

      updateData: (
        id: IncidentWorkItemId,
        formId: IncidentFormId,
        section: FormSection,
        data: Record<string, unknown>,
        validateData: boolean,
        status?: IncidentWorkItemStatus,
      ) =>
        apiAxios.request<ResponseBody<NoContentBody, ErrorBody>>(
          config.incidentWorkItem.updateData(
            id,
            formId,
            section,
            data,
            validateData,
            status,
          ),
        ),

      create136Form: (id: IncidentWorkItemId) =>
        apiAxios.request(config.incidentWorkItem.create136Form(id)),

      createMentalCapacityActForm: (id: IncidentWorkItemId) =>
        apiAxios.request(
          config.incidentWorkItem.createMentalCapacityActForm(id),
        ),

      createPlaceOfSafetyForm: (
        workItemId: IncidentWorkItemId,
        formId: IncidentFormId,
      ) =>
        apiAxios.request(
          config.incidentWorkItem.createPlaceOfSafetyForm(workItemId, formId),
        ),

      createAndPopulatePlaceOfSafety: (
        workItemId: IncidentWorkItemId,
        formId: IncidentFormId,
        data: Record<string, unknown>,
      ) =>
        apiAxios.request<ResponseBody<NoContentBody, ErrorBody>>(
          config.incidentWorkItem.createAndPopulatePlaceOfSafety(
            workItemId,
            formId,
            data,
          ),
        ),

      upsertPlaceOfSafety: (
        workItemId: IncidentWorkItemId,
        formId: IncidentFormId,
        body: {
          createForm: boolean;
          mainSectionData: Record<string, unknown>;
          mainSectionValidateData: boolean;
          arrivalSectionData?: Record<string, unknown> | null;
          arrivalSectionValidateData?: boolean;
          arrivalStatus?: IncidentWorkItemStatus;
          leaveSectionData?: Record<string, unknown> | null;
          leaveSectionValidateData?: boolean;
        },
      ) =>
        apiAxios.request<ResponseBody<NoContentBody, ErrorBody>>(
          config.incidentWorkItem.upsertPlaceOfSafety(workItemId, formId, body),
        ),

      recordPolicePresenceRequestAsOfficer: (
        workItemId: IncidentWorkItemId,
        formId: IncidentFormId,
        data: {
          [FormSectionSchema.enum.requestPolicePresenceMain]: Record<
            string,
            unknown
          >;
          [FormSectionSchema.enum.recordPolicePresence]?: Record<
            string,
            unknown
          >;
        },
      ) =>
        apiAxios.request(
          config.incidentWorkItem.recordPolicePresenceRequestAsOfficer(
            workItemId,
            formId,
            data,
          ),
        ),

      updateActiveOfficerRequest: (
        incidentWorkItemId: IncidentWorkItemId,
        officerRequestFormId: IncidentFormId | null,
      ) =>
        apiAxios.request<ResponseBody<NoContentBody, ErrorBody>>(
          config.incidentWorkItem.updateActiveOfficerRequest(
            incidentWorkItemId,
            officerRequestFormId,
          ),
        ),

      createPreEndIncidentForm: (
        workItemId: IncidentWorkItemId,
        formId: IncidentFormId,
      ) =>
        apiAxios.request(
          config.incidentWorkItem.createPreEndIncidentForm(workItemId, formId),
        ),

      createFinishForm: (
        workItemId: IncidentWorkItemId,
        formId: IncidentFormId,
      ) =>
        apiAxios.request(
          config.incidentWorkItem.createFinishForm(workItemId, formId),
        ),

      endIncident: (workItemId: IncidentWorkItemId) =>
        apiAxios.request<ResponseBody<NoContentBody, ErrorBody>>(
          config.incidentWorkItem.endIncident(workItemId),
        ),

      preEndAndCloseIncident: (
        workItemId: IncidentWorkItemId,
        formId: IncidentFormId,
        sectionId: FormSection,
        data: Record<string, unknown>,
      ) =>
        apiAxios.request<ResponseBody<NoContentBody, ErrorBody>>(
          config.incidentWorkItem.preEndAndCloseIncident(
            workItemId,
            formId,
            sectionId,
            data,
          ),
        ),

      finishAndCloseIncident: (
        workItemId: IncidentWorkItemId,
        formId: IncidentFormId,
        sectionId: FormSection,
        data: Record<string, unknown>,
      ) =>
        apiAxios.request<ResponseBody<NoContentBody, ErrorBody>>(
          config.incidentWorkItem.finishAndCloseIncident(
            workItemId,
            formId,
            sectionId,
            data,
          ),
        ),

      submitTriage: (
        workItemId: IncidentWorkItemId,
        formId: IncidentFormId,
        sectionId: FormSection,
        data: Record<string, unknown>,
      ) =>
        apiAxios.request<ResponseBody<NoContentBody, ErrorBody>>(
          config.incidentWorkItem.submitTriage(
            workItemId,
            formId,
            sectionId,
            data,
          ),
        ),

      createTransfer(createTransfer: CreateTransferRequestUnbranded) {
        return apiAxios.request<ResponseBody<NoContentBody, ErrorBody>>(
          config.incidentWorkItem.createTransfer(createTransfer),
        );
      },

      resolveTransfer: (resolveTransfer: ResolveTransferRequestUnbranded) => {
        return apiAxios.request(
          config.incidentWorkItem.resolveTransfer(resolveTransfer),
        );
      },

      revokeTransfer: (incidentWorkItemId: RevokeTransferRequestUnbranded) => {
        return apiAxios.request(
          config.incidentWorkItem.revokeTransfer(incidentWorkItemId),
        );
      },
      adminRevokeTransfer: (
        incidentWorkItemId: RevokeTransferRequestUnbranded,
      ) => {
        return apiAxios.request(
          config.incidentWorkItem.adminRevokeTransfer(incidentWorkItemId),
        );
      },
      search: (filters: SearchIncidentWorkItemRequest) => {
        return apiAxios.request<SearchIncidentWorkItemResponse>(
          config.incidentWorkItem.search(filters),
        );
      },
      createSnapshotDownload: (id: IncidentWorkItemId) => {
        return apiAxios.request<{ downloadId: string }>(
          config.incidentWorkItem.createSnapshotDownload(id),
        );
      },
      pollSnapshotDownload: (id: IncidentWorkItemId, jobId: string) => {
        return apiAxios.request<
          | { status: "pending" }
          | { status: "completed"; downloadUrl: string }
          | { status: "failed"; errorMessage: string }
        >(config.incidentWorkItem.pollSnapshotDownload(id, jobId));
      },
      getSummary: (id: IncidentWorkItemId) =>
        apiAxios.request<IncidentWorkItemWithContext | null>(
          config.incidentWorkItem.getSummary(id),
        ),
      getAuditTrail: (
        id: IncidentWorkItemId,
        params: { limit?: number; offset?: number } = {},
      ) =>
        apiAxios.request<GetAuditTrailEntriesResponse>(
          config.incidentWorkItem.getAuditTrail(id, params),
        ),
      adminEditReportingFields: (
        id: IncidentWorkItemId,
        body: AdminEditReportingFieldsRequestUnbranded,
      ) =>
        apiAxios.request(
          config.incidentWorkItem.adminEditReportingFields(id, body),
        ),
      adminRelinkPlaceOfSafety: (
        id: IncidentWorkItemId,
        body: AdminRelinkPlaceOfSafetyRequestUnbranded,
      ) =>
        apiAxios.request(
          config.incidentWorkItem.adminRelinkPlaceOfSafety(id, body),
        ),
    },
  };
};

export const apiHooks = () => {
  return {
    incidentWorkItem: {
      get: (id: IncidentWorkItemId) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        return usePusherChannelReload<IncidentWorkItemWithContext | undefined>(
          notificationChannelFns.vision.police(id),
          config.incidentWorkItem.get(id),
        );
      },
      getMyActiveIncidentWorkItems: function useGetMyActiveIncidentWorkItem(
        userId: UserId | undefined,
      ) {
        return usePusherChannelReload<GetIncidentWorkItemResponse | undefined>(
          notificationChannelFns.user.notifications(userId ?? ""),
          config.incidentWorkItem.getMyActiveIncidentWorkItems(),
        );
      },
      getArchiveForms: function useGetArchiveForms(userId: UserId | undefined) {
        return usePusherChannelReload<GetIncidentWorkItemResponse | undefined>(
          notificationChannelFns.vision.police(userId ?? ""),
          config.incidentWorkItem.getArchiveForms(),
        );
      },
      getPdf: (id: IncidentWorkItemId) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        return usePusherChannelReload<string | null>(
          notificationChannelFns.vision.police(id),
          config.incidentWorkItem.getPdf(id),
        );
      },
      search: (filters: SearchIncidentWorkItemRequest) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        return useApiAxios<SearchIncidentWorkItemResponse>(
          config.incidentWorkItem.search(filters),
        );
      },
      getSummary: (id: IncidentWorkItemId) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        return useApiAxios<IncidentWorkItemWithContext | null>(
          config.incidentWorkItem.getSummary(id),
        );
      },
      getAuditTrail: (
        id: IncidentWorkItemId,
        params: { limit?: number; offset?: number } = {},
      ) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        return useApiAxios<GetAuditTrailEntriesResponse>(
          config.incidentWorkItem.getAuditTrail(id, params),
        );
      },
    },
  };
};
